import React from "react";
import { appConstants } from "JS/Helpers/Constants";

export const isChunkLoadError = (error: any): boolean => {
    if (!error) return false;
    if (error.name === "ChunkLoadError") return true;

    const message = typeof error.message === "string" ? error.message : "";

    return (
        /Loading (CSS )?chunk [^\s]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message)
    );
};

export const clearChunkReloadMark = () => {
    try {
        window.sessionStorage.removeItem(
            appConstants.sessionStorage.chunkReloadAttempted,
        );
    } catch {
        /* nothing to do */
    }
};

interface ChunkLoadErrorBoundaryProps {
    children?: React.ReactNode;
    fallback?: React.ReactNode;
}

interface ChunkLoadErrorBoundaryState {
    error: any;
}

export class ChunkLoadErrorBoundary extends React.Component<
    ChunkLoadErrorBoundaryProps,
    ChunkLoadErrorBoundaryState
> {
    constructor(props: ChunkLoadErrorBoundaryProps) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error: any) {
        return { error };
    }

    componentDidCatch(error: any) {
        if (!isChunkLoadError(error) || this.hasAlreadyReloaded()) return;

        this.markReloaded();
        window.location.reload();
    }

    private hasAlreadyReloaded() {
        try {
            return (
                window.sessionStorage.getItem(
                    appConstants.sessionStorage.chunkReloadAttempted,
                ) === "true"
            );
        } catch {
            // Private-mode Safari can throw on sessionStorage access.
            return true;
        }
    }

    private markReloaded() {
        try {
            window.sessionStorage.setItem(
                appConstants.sessionStorage.chunkReloadAttempted,
                "true",
            );
        } catch {
            /* nothing to do — the reload just won't be retried */
        }
    }

    render() {
        const { error } = this.state;

        if (error && !isChunkLoadError(error)) throw error;

        if (error) return this.props.fallback ?? null;

        return this.props.children;
    }
}
